import React from 'react';
import { Requisition } from '../../types';
import { EMPTY_ITEM } from './requisitionWorkflowHelpers';

export type ItemFormValues = typeof EMPTY_ITEM;

interface RequisitionItemFormProps {
  requisitions: Requisition[];
  editing: boolean;
  values: ItemFormValues;
  onChange: (values: ItemFormValues) => void;
  requisitionId: string;
  onRequisitionChange: (id: string) => void;
  onSubmit: (e: React.FormEvent) => void;
  onCancel: () => void;
  employeeOptions: { value: string; label: string }[];
  supplierOptions: { value: string; label: string }[];
}

const FIELDS = [
  { label: 'Item Name', field: 'item', type: 'text', required: true, placeholder: 'e.g., Marine Paint' },
  { label: 'Quantity', field: 'qty', type: 'number', required: true, placeholder: '0' },
  { label: 'ROB (Remaining on Board)', field: 'rob', type: 'text', placeholder: 'e.g., 5 ltr' },
  { label: 'Approved Quantity', field: 'appQty', type: 'number', placeholder: '0' },
  { label: 'Unit Price (EGP)', field: 'unitPrice', type: 'number', placeholder: '0.00', step: '0.01' },
];

const RequisitionItemForm: React.FC<RequisitionItemFormProps> = ({
  requisitions, editing, values, onChange, requisitionId, onRequisitionChange, onSubmit, onCancel, employeeOptions, supplierOptions,
}) => (
  <div className="card p-6">
    <h3 className="text-lg font-semibold text-gray-900 mb-4">{editing ? 'Edit Requisition Item' : 'Add New Requisition Item'}</h3>
    <form onSubmit={onSubmit} className="space-y-4">
      {!editing && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Requisition <span className="text-red-500">*</span></label>
          <select value={requisitionId} onChange={(e) => onRequisitionChange(e.target.value)} required
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-ocean-500">
            <option value="">Select requisition...</option>
            {requisitions.map((r) => (
              <option key={r.id} value={r.documentId || r.id}>{r.formNumber} — {r.vessel}</option>
            ))}
          </select>
        </div>
      )}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {FIELDS.map(({ label, field, type, required, placeholder, step }) => (
          <div key={field}>
            <label className="block text-sm font-medium text-gray-700 mb-1">{label} {required && <span className="text-red-500">*</span>}</label>
            <input type={type} value={(values as any)[field]} step={step}
              onChange={(e) => onChange({ ...values, [field]: type === 'number' ? (parseFloat(e.target.value) || 0) : e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-ocean-500"
              placeholder={placeholder} required={required} />
          </div>
        ))}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Supplier</label>
          <select value={values.supplier} onChange={(e) => onChange({ ...values, supplier: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-ocean-500">
            <option value="">Select supplier...</option>
            {supplierOptions.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Prepared By</label>
          <select value={values.preparedBy} onChange={(e) => onChange({ ...values, preparedBy: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-ocean-500">
            <option value="">Select employee...</option>
            {employeeOptions.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </div>
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Remarks</label>
        <textarea value={values.remarks} onChange={(e) => onChange({ ...values, remarks: e.target.value })} rows={2}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-ocean-500" placeholder="Any additional notes..." />
      </div>
      <div className="flex justify-end space-x-3">
        <button type="button" onClick={onCancel} className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50">Cancel</button>
        <button type="submit" className="px-4 py-2 bg-ocean-600 text-white rounded-lg hover:bg-ocean-700">{editing ? 'Update Item' : 'Add Item'}</button>
      </div>
    </form>
  </div>
);

export default RequisitionItemForm;
